import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WorkMeshMark } from "@/components/WorkMeshLogo";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, Sparkle, ShieldCheck, Graph, Users, ChartLineUp, Trophy,
  Robot, Plugs, Storefront, Coins, Certificate, Buildings, CheckCircle, PlayCircle, Lightning,
} from "@phosphor-icons/react";
import "./Landing.css";

const API = process.env.REACT_APP_BACKEND_URL;

/* -------------------- animated mesh background -------------------- */
const MESH_NODES = [
  { x: 12, y: 22 }, { x: 28, y: 12 }, { x: 44, y: 26 }, { x: 62, y: 14 },
  { x: 82, y: 24 }, { x: 92, y: 42 }, { x: 74, y: 46 }, { x: 55, y: 40 },
  { x: 38, y: 52 }, { x: 18, y: 44 }, { x: 8, y: 62 }, { x: 26, y: 74 },
  { x: 46, y: 80 }, { x: 66, y: 72 }, { x: 84, y: 82 }, { x: 60, y: 60 },
];
const MESH_EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 2], [7, 8],
  [8, 9], [9, 0], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [13, 15],
  [15, 7], [15, 8], [15, 6], [2, 7], [8, 12], [3, 7],
];

function MeshBackdrop({ opacity = 0.9 }) {
  return (
    <svg
      viewBox="0 0 100 90"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity }}
      aria-hidden
    >
      {MESH_EDGES.map(([a, b], i) => {
        const A = MESH_NODES[a]; const B = MESH_NODES[b];
        return (
          <line
            key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y}
            className="wm-mesh-line"
            stroke="currentColor" strokeWidth="0.12" opacity="0.35"
            style={{ animationDelay: `${(i * 0.13).toFixed(2)}s` }}
          />
        );
      })}
      {MESH_NODES.map((n, i) => (
        <g key={i}>
          <circle
            cx={n.x} cy={n.y} r="0.9"
            className="wm-node-pulse"
            fill={i % 5 === 0 ? "#E1252A" : "currentColor"}
            style={{ animationDelay: `${(i * 0.19).toFixed(2)}s` }}
          />
          <circle cx={n.x} cy={n.y} r="0.35" fill="currentColor" opacity="0.9" />
        </g>
      ))}
    </svg>
  );
}

/* -------------------- top nav -------------------- */
function LandingNav({ onDemoClick }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-background/80 border-b border-border"
          : "bg-transparent"
      }`}
      data-testid="landing-nav"
    >
      <div className="bg-foreground text-background text-center text-[10px] uppercase tracking-[0.25em] py-1.5 px-4" data-testid="demo-banner">
        Live demo preview — explore WorkMesh freely with simulated enterprise data
      </div>
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
        <div className="flex items-center gap-3">
          <WorkMeshMark size={32} />
          <div>
            <div
              className="text-xl font-bold tracking-tight leading-none"
              style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
            >
              Work<span className="text-[#E1252A]">Mesh</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mt-0.5">
              Internal Talent Marketplace
            </div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-8 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          <a href="#capabilities" className="hover:text-foreground transition-colors" data-testid="nav-capabilities">Capabilities</a>
          <a href="#graph" className="hover:text-foreground transition-colors" data-testid="nav-graph">Graph</a>
          <a href="#ai-team" className="hover:text-foreground transition-colors" data-testid="nav-ai-team">AI Team Builder</a>
          <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="#contact" className="hover:text-foreground transition-colors" data-testid="nav-contact">Contact Sales</a>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="text-[11px] uppercase tracking-[0.22em] px-3 py-2 hover:text-foreground text-zinc-600 hidden sm:inline"
            data-testid="nav-login"
          >
            Sign in
          </Link>
          <Button
            onClick={onDemoClick}
            className="rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200 text-[11px] uppercase tracking-[0.22em] h-9 px-4"
            data-testid="nav-demo-cta"
          >
            Request Demo <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </header>
  );
}

/* -------------------- hero -------------------- */
function Hero({ onDemoClick }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.4]);

  return (
    <section
      ref={ref}
      className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 border-b border-border"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 wm-grid-bg pointer-events-none" />
      <motion.div style={{ y, opacity }} className="absolute inset-0 text-foreground">
        <MeshBackdrop opacity={0.55} />
      </motion.div>
      <div
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle,#E1252A22,transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2 border border-border px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.3em] text-zinc-600 dark:text-zinc-400"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E1252A] animate-pulse" />
              For enterprise talent leaders · SSO ready
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-bold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
              style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
            >
              Your workforce{" "}
              <span className="relative inline-block">
                <span className="relative z-10">connected</span>
                <motion.span
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 1.1, ease: "easeOut" }}
                  className="absolute left-0 right-0 -bottom-1 h-3 bg-[#E1252A]/25 origin-left z-0"
                />
              </span>
              <br />
              like never before.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="max-w-xl text-base sm:text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed"
            >
              WorkMesh is the internal talent marketplace that turns every employee into a discoverable
              expert. Post work across departments, form AI-picked teams in seconds, and grow measurable
              capability with a live organizational skills graph.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <Button
                onClick={onDemoClick}
                className="rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200 h-12 px-6 text-[12px] uppercase tracking-[0.22em]"
                data-testid="hero-demo-cta"
              >
                Request Enterprise Demo <ArrowRight size={16} className="ml-2" />
              </Button>
              <Link to="/login" data-testid="hero-try-cta">
                <Button
                  variant="outline"
                  className="rounded-sm h-12 px-6 text-[12px] uppercase tracking-[0.22em] w-full sm:w-auto"
                >
                  <PlayCircle size={18} className="mr-2" /> Try live demo
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.9 }}
              className="flex flex-wrap gap-6 pt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500"
            >
              <span className="flex items-center gap-2"><ShieldCheck size={14} /> SSO + JWT</span>
              <span className="flex items-center gap-2"><Sparkle size={14} /> Claude Sonnet 4.5</span>
              <span className="flex items-center gap-2"><Plugs size={14} /> SAP · Workday plugin</span>
              <span className="flex items-center gap-2"><Graph size={14} /> Capability Graph</span>
            </motion.div>
          </div>

          {/* orbit card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="lg:col-span-5"
          >
            <HeroOrbitCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HeroOrbitCard() {
  const roles = ["Data Scientist", "Cloud Architect", "Product Manager", "UX Researcher", "ML Engineer"];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % roles.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative border border-border bg-background/60 backdrop-blur-sm p-8 rounded-sm overflow-hidden">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
        <span>Live · your org</span>
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Streaming
        </span>
      </div>

      <div className="relative h-72 flex items-center justify-center">
        {/* rings */}
        <div className="absolute w-40 h-40 rounded-full border border-border" />
        <div className="absolute w-64 h-64 rounded-full border border-border/70" />
        <div className="absolute w-[19rem] h-[19rem] rounded-full border border-border/40" />

        {/* orbiting nodes */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
          <motion.div
            key={k}
            className="absolute w-8 h-8 rounded-full border border-border bg-background flex items-center justify-center text-[10px] font-mono"
            animate={{ rotate: 360 }}
            transition={{ duration: 18 + k * 2, repeat: Infinity, ease: "linear" }}
            style={{
              width: 220 + (k % 3) * 40,
              height: 220 + (k % 3) * 40,
              transform: `translate(-50%,-50%)`,
              left: "50%", top: "50%",
            }}
          >
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{
                position: "absolute", top: -5, left: "50%",
                background: k % 4 === 0 ? "#E1252A" : "currentColor",
              }}
            />
          </motion.div>
        ))}

        {/* central card */}
        <div className="relative z-10 w-40 h-40 border border-foreground bg-background flex flex-col items-center justify-center rounded-sm shadow-xl">
          <WorkMeshMark size={36} />
          <div className="mt-3 text-[9px] uppercase tracking-[0.28em] text-zinc-500">Matched role</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={roles[i]}
              initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }} transition={{ duration: 0.35 }}
              className="mt-1 text-sm font-semibold text-center px-2"
            >
              {roles[i]}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-6">
        <MetricPill label="Employees" value="5,000+" />
        <MetricPill label="Capabilities" value="119" />
        <MetricPill label="Evidence" value="20k" accent />
      </div>
    </div>
  );
}

function MetricPill({ label, value, accent }) {
  return (
    <div className="border border-border p-3 rounded-sm">
      <div className={`text-lg font-bold font-mono ${accent ? "text-[#E1252A]" : ""}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

/* -------------------- logo marquee -------------------- */
function TrustBar() {
  const words = [
    "SSO Ready", "GDPR Aligned", "Audit-Grade Ledger", "RBAC · 5 Roles",
    "Claude Sonnet 4.5", "SAP Successfactors", "Workday", "Slack · Teams",
    "Custom Workflows", "Zero-copy Deploy",
  ];
  return (
    <section className="relative border-b border-border py-6 overflow-hidden bg-background" aria-label="Trust markers">
      <div className="wm-marquee-track flex gap-14 whitespace-nowrap text-[11px] uppercase tracking-[0.3em] text-zinc-500">
        {[...words, ...words].map((w, i) => (
          <span key={i} className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 bg-[#E1252A]" />
            {w}
          </span>
        ))}
      </div>
    </section>
  );
}

/* -------------------- features -------------------- */
const FEATURES = [
  { icon: Storefront, title: "Task Marketplace", body: "Cross-department task board with intelligent routing, self-pick for quick work, and manager approvals for high-impact bets." },
  { icon: Graph, title: "Organizational Capability Graph", body: "Every skill, employee, and outcome mapped into a live graph. Spot bench strength, hidden experts, and gaps instantly." },
  { icon: Robot, title: "AI Team Builder", body: "Describe the initiative. Claude Sonnet 4.5 assembles the optimal team from your talent pool with skill coverage and confidence." },
  { icon: Coins, title: "Task Credits Economy", body: "Department wallets fund work. Employees earn credits and impact points, with rollover policies you configure." },
  { icon: Trophy, title: "Reputation & Reviews", body: "Peer reviews, verified evidence, and badge systems turn every completed task into durable career capital." },
  { icon: ChartLineUp, title: "Executive Dashboards", body: "Track org-wide throughput, capability maturity, and dept×domain heatmaps at C-suite fidelity." },
  { icon: Plugs, title: "HR Plugin System", body: "Ship-day integration with Workday and SAP SuccessFactors. Roster, dept structure, and skill catalogs auto-sync." },
  { icon: Lightning, title: "Configurable Workflow Engine", body: "Model approvals, escalation, and multi-reviewer scoring without code. Enterprise-grade audit trail." },
];

function FeatureGrid() {
  return (
    <section id="capabilities" className="relative py-24 lg:py-32 border-b border-border" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">01 · Capabilities</div>
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
          >
            Everything you need to run an internal talent marketplace at enterprise scale.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
                className="wm-hover-lift group bg-background p-7 border border-transparent"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-11 h-11 border border-border rounded-sm flex items-center justify-center mb-5 group-hover:bg-[#E1252A] group-hover:text-white group-hover:border-[#E1252A] transition-colors">
                  <Icon size={22} weight="duotone" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2 font-mono">
                  0{i + 1}
                </div>
                <h3 className="text-lg font-semibold mb-2 tracking-tight">{f.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- capability graph section -------------------- */
const GRAPH_NODES = [
  { id: "eng", label: "Engineering", x: 20, y: 40, color: "#E1252A", size: 34 },
  { id: "data", label: "Data & Analytics", x: 45, y: 22, color: "#7c3aed", size: 30 },
  { id: "biz", label: "Business", x: 78, y: 32, color: "#0ea5e9", size: 30 },
  { id: "lead", label: "Leadership", x: 82, y: 66, color: "#059669", size: 28 },
  { id: "tools", label: "Tools & Platforms", x: 30, y: 76, color: "#f59e0b", size: 26 },

  { id: "py", label: "Python", x: 12, y: 28, color: "#E1252A", size: 14 },
  { id: "cloud", label: "Cloud", x: 8, y: 52, color: "#E1252A", size: 14 },
  { id: "react", label: "React", x: 26, y: 58, color: "#E1252A", size: 12 },

  { id: "ml", label: "ML/AI", x: 40, y: 8, color: "#7c3aed", size: 14 },
  { id: "sql", label: "SQL", x: 55, y: 12, color: "#7c3aed", size: 12 },
  { id: "viz", label: "Analytics", x: 60, y: 40, color: "#7c3aed", size: 12 },

  { id: "seo", label: "SEO", x: 90, y: 20, color: "#0ea5e9", size: 12 },
  { id: "content", label: "Content", x: 72, y: 12, color: "#0ea5e9", size: 12 },
  { id: "ops", label: "Ops", x: 68, y: 52, color: "#0ea5e9", size: 12 },

  { id: "coach", label: "Coaching", x: 92, y: 78, color: "#059669", size: 12 },
  { id: "prod", label: "Product Strategy", x: 74, y: 84, color: "#059669", size: 14 },
];
const GRAPH_EDGES = [
  ["eng", "data"], ["eng", "tools"], ["eng", "py"], ["eng", "cloud"], ["eng", "react"],
  ["data", "ml"], ["data", "sql"], ["data", "viz"], ["data", "biz"], ["data", "eng"],
  ["biz", "seo"], ["biz", "content"], ["biz", "ops"], ["biz", "lead"],
  ["lead", "coach"], ["lead", "prod"], ["prod", "biz"], ["viz", "biz"], ["ops", "eng"],
  ["tools", "py"], ["tools", "cloud"], ["tools", "react"], ["ml", "py"], ["cloud", "eng"],
];
function CapabilityGraphSection() {
  const [active, setActive] = useState("data");
  const activeNode = GRAPH_NODES.find((n) => n.id === active);
  return (
    <section id="graph" className="relative py-24 lg:py-32 border-b border-border" data-testid="graph-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">02 · Capability Graph</div>
            <h2
              className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-6"
              style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
            >
              A living map of<br /> what your people <span className="text-[#E1252A]">can do</span>.
            </h2>
            <p className="text-zinc-500 leading-relaxed mb-8">
              Every certification, project, endorsement, and completed task becomes evidence
              on a node. Instantly see who has the depth — and where the org is under-invested.
            </p>
            <ul className="space-y-3 text-sm">
              {[
                "119 capabilities across 5 domains, auto-classified",
                "20,000+ evidence records with confidence scoring",
                "Real-time gap detection vs. strategic priorities",
                "Expert search: match % · years · availability",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-[#E1252A] mt-0.5 flex-shrink-0" weight="fill" />
                  <span className="text-zinc-700 dark:text-zinc-300">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="relative border border-border rounded-sm bg-background aspect-[5/4] overflow-hidden">
              <div className="absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                Capability graph · demo view
              </div>
              <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Interactive
              </div>

              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                {GRAPH_EDGES.map(([a, b], i) => {
                  const A = GRAPH_NODES.find((n) => n.id === a);
                  const B = GRAPH_NODES.find((n) => n.id === b);
                  const highlight = a === active || b === active;
                  return (
                    <line
                      key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                      stroke={highlight ? "#E1252A" : "currentColor"}
                      strokeWidth={highlight ? 0.3 : 0.15}
                      opacity={highlight ? 0.9 : 0.25}
                      className="transition-all duration-500"
                    />
                  );
                })}
              </svg>

              {GRAPH_NODES.map((n) => {
                const isActive = n.id === active;
                return (
                  <button
                    key={n.id}
                    onClick={() => setActive(n.id)}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none"
                    style={{ left: `${n.x}%`, top: `${n.y}%` }}
                    data-testid={`graph-node-${n.id}`}
                    aria-label={n.label}
                  >
                    <motion.div
                      animate={{ scale: isActive ? 1.2 : 1 }}
                      transition={{ type: "spring", stiffness: 220, damping: 18 }}
                      className="rounded-full border-2 flex items-center justify-center"
                      style={{
                        width: n.size, height: n.size,
                        background: isActive ? n.color : `${n.color}22`,
                        borderColor: n.color,
                        boxShadow: isActive ? `0 0 0 6px ${n.color}22` : "none",
                      }}
                    >
                      <div
                        className="rounded-full"
                        style={{
                          width: n.size * 0.4, height: n.size * 0.4,
                          background: isActive ? "#fff" : n.color,
                        }}
                      />
                    </motion.div>
                    <div
                      className={`absolute left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.15em] transition-opacity ${
                        isActive ? "opacity-100" : "opacity-60 group-hover:opacity-100"
                      }`}
                      style={{ color: n.color }}
                    >
                      {n.label}
                    </div>
                  </button>
                );
              })}

              {/* detail card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-64 border border-border bg-background/95 backdrop-blur-sm p-4 rounded-sm"
                >
                  <div className="text-[9px] uppercase tracking-[0.28em] text-zinc-500 mb-1">Selected node</div>
                  <div className="text-base font-semibold mb-3" style={{ color: activeNode.color }}>
                    {activeNode.label}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold font-mono">{20 + Math.floor(active.length * 3)}</div>
                      <div className="text-[8px] uppercase tracking-[0.2em] text-zinc-500">Experts</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold font-mono text-[#E1252A]">{60 + active.length * 4}%</div>
                      <div className="text-[8px] uppercase tracking-[0.2em] text-zinc-500">Coverage</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold font-mono">{active.length + 3}</div>
                      <div className="text-[8px] uppercase tracking-[0.2em] text-zinc-500">Open Tasks</div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- AI Team Builder demo -------------------- */
const SAMPLE_TEAM = [
  { name: "Priya Iyer", role: "Cloud Architect", coverage: 96, skills: ["Cloud", "Python", "Kafka"], color: "#E1252A" },
  { name: "Dev Kapoor", role: "ML Engineer", coverage: 92, skills: ["ML/AI", "Python", "MLOps"], color: "#7c3aed" },
  { name: "Riya Sen", role: "Backend Engineer", coverage: 88, skills: ["Python", "Security"], color: "#0ea5e9" },
  { name: "Owen Park", role: "Product Lead", coverage: 84, skills: ["Product", "UX"], color: "#059669" },
];
const DEMO_BRIEFS = [
  "Ship an AI incident-triage assistant in 6 weeks.",
  "Migrate billing to event-driven architecture.",
  "Launch a customer churn analytics dashboard.",
];

function AITeamBuilderSection() {
  const [brief, setBrief] = useState(DEMO_BRIEFS[0]);
  const [phase, setPhase] = useState("idle"); // idle · scanning · matched
  const [team, setTeam] = useState([]);

  const run = () => {
    setPhase("scanning");
    setTeam([]);
    setTimeout(() => {
      setTeam(SAMPLE_TEAM);
      setPhase("matched");
    }, 1800);
  };

  useEffect(() => {
    const t = setTimeout(run, 700);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="ai-team" className="relative py-24 lg:py-32 border-b border-border overflow-hidden" data-testid="ai-team-section">
      <div className="absolute inset-0 wm-grid-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">03 · AI Team Builder</div>
            <h2
              className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-6"
              style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
            >
              Build a team with <span className="text-[#E1252A]">AI</span>,<br /> from your own talent pool.
            </h2>
            <p className="text-zinc-500 leading-relaxed mb-6">
              Describe your initiative in plain English. WorkMesh&apos;s Team Advisor
              (Claude Sonnet 4.5) scans capability, workload, availability, and reputation
              across your entire org — and returns the highest-confidence team.
            </p>
            <ul className="space-y-3 text-sm mb-8">
              {[
                "Explains why each member was chosen",
                "Shows skill coverage bars per candidate",
                "Honors workload caps and RBAC",
                "One-click convert to marketplace task",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Sparkle size={16} className="text-[#E1252A] mt-1 flex-shrink-0" weight="fill" />
                  <span className="text-zinc-700 dark:text-zinc-300">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-border rounded-sm bg-background overflow-hidden">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">AI Team Advisor · live demo</div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em]">
                  <span className={`w-1.5 h-1.5 rounded-full ${phase === "scanning" ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />
                  {phase === "scanning" ? "Scanning" : "Ready"}
                </div>
              </div>

              <div className="p-5 space-y-4">
                <Label className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Initiative brief</Label>
                <div className="flex gap-2 flex-wrap">
                  {DEMO_BRIEFS.map((b) => (
                    <button
                      key={b}
                      onClick={() => setBrief(b)}
                      className={`text-[11px] px-3 py-1.5 border rounded-sm transition-colors ${
                        b === brief ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"
                      }`}
                      data-testid={`brief-preset-${b.slice(0, 12)}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
                <Textarea
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  className="rounded-sm min-h-[80px] text-sm"
                  data-testid="ai-team-brief-input"
                />
                <Button
                  onClick={run}
                  className="w-full sm:w-auto rounded-sm bg-[#E1252A] hover:bg-[#c11f24] text-white h-11 px-6 text-[11px] uppercase tracking-[0.22em]"
                  data-testid="ai-team-run-button"
                >
                  <Robot size={16} className="mr-2" /> Assemble team
                </Button>
              </div>

              <div className="border-t border-border p-5 min-h-[280px]">
                {phase === "scanning" && <ScanningState />}
                {phase === "matched" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Recommended team · 91% confidence</div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-600 flex items-center gap-1">
                        <CheckCircle size={14} weight="fill" /> Skill coverage 95%
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {team.map((m, i) => (
                        <motion.div
                          key={m.name}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: i * 0.1 }}
                          className="border border-border p-4 rounded-sm hover:border-foreground/50 transition-colors"
                          data-testid={`team-member-card-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                              style={{ background: m.color }}
                            >
                              {m.name.split(" ").map((n) => n[0]).join("")}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm truncate">{m.name}</div>
                              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{m.role}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold font-mono text-[#E1252A]">{m.coverage}%</div>
                              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">match</div>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1">
                            {m.skills.map((s) => (
                              <span key={s} className="text-[9px] px-2 py-0.5 border border-border rounded-sm font-mono">
                                {s}
                              </span>
                            ))}
                          </div>
                          <div className="mt-3 h-1 bg-border overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${m.coverage}%` }}
                              transition={{ duration: 0.8, delay: i * 0.15 }}
                              className="h-full"
                              style={{ background: m.color }}
                            />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScanningState() {
  const steps = [
    "Parsing initiative brief…",
    "Scanning 5,000+ employees…",
    "Ranking by capability coverage…",
    "Balancing workload & availability…",
    "Composing final team…",
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => Math.min(v + 1, steps.length - 1)), 320);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="space-y-3 py-4">
      {steps.slice(0, i + 1).map((s, idx) => (
        <motion.div
          key={s}
          initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 text-sm"
        >
          {idx < i ? (
            <CheckCircle size={16} weight="fill" className="text-emerald-500" />
          ) : (
            <div className="w-4 h-4 border-2 border-[#E1252A] border-t-transparent rounded-full animate-spin" />
          )}
          <span className={idx < i ? "text-zinc-500" : "text-foreground"}>{s}</span>
        </motion.div>
      ))}
    </div>
  );
}

/* -------------------- how it works -------------------- */
const STEPS = [
  { n: "01", title: "Post the work", body: "Any employee, manager, or admin posts a task with skills, effort, and reward. AI enriches skill tags automatically." },
  { n: "02", title: "Match with talent", body: "The capability graph surfaces best-fit experts, or Team Advisor assembles a full team. Employees can also self-pick short work." },
  { n: "03", title: "Deliver & grow", body: "Reviewers score submissions. Credits flow. Reputation compounds. Every completed task strengthens the org's capability graph." },
];
function HowItWorks() {
  return (
    <section className="relative py-24 lg:py-32 border-b border-border bg-zinc-50 dark:bg-zinc-950/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">04 · How it works</div>
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
          >
            From idea to delivery in three moves.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 relative">
          <div className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-px bg-border" />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="relative bg-background border border-border p-8 rounded-sm"
              data-testid={`how-step-${i}`}
            >
              <div className="w-14 h-14 rounded-full bg-foreground text-background flex items-center justify-center font-mono font-bold text-sm mb-6 relative z-10">
                {s.n}
              </div>
              <h3 className="text-xl font-semibold mb-3 tracking-tight">{s.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- big number strip -------------------- */
function ImpactStrip() {
  const stats = [
    { v: "5,000+", l: "Employees simulated" },
    { v: "119", l: "Capabilities mapped" },
    { v: "20k+", l: "Evidence records" },
    { v: "2 yrs", l: "Historical activity" },
  ];
  return (
    <section className="relative py-16 border-b border-border bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="border-l-2 border-[#E1252A] pl-4"
          >
            <div
              className="text-4xl lg:text-5xl font-bold font-mono tracking-tight"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {s.v}
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-background/60 mt-2">{s.l}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* -------------------- roles / who it's for -------------------- */
const AUDIENCE = [
  { role: "CHRO / CPO", body: "Operationalize skill strategy with a live capability index and gap analysis rooted in real work." },
  { role: "Head of L&D", body: "Turn every completed task into evidence and measurable skill growth — no self-reporting required." },
  { role: "Engineering Leaders", body: "Route short work to hidden experts. Free senior ICs from Slack triage." },
  { role: "COO / Ops", body: "Cross-team collaboration with audit-grade approvals, credit budgets, and workload governance." },
];
function AudienceSection() {
  return (
    <section className="py-24 lg:py-32 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">05 · Built for</div>
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
          >
            Talent, HR, and operations leaders who refuse to guess.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {AUDIENCE.map((a, i) => (
            <motion.div
              key={a.role}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="border border-border p-6 rounded-sm wm-hover-lift"
              data-testid={`audience-${i}`}
            >
              <Users size={24} weight="duotone" className="text-[#E1252A] mb-4" />
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2">{a.role}</div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{a.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- pricing -------------------- */
const PLANS = [
  {
    key: "startup",
    title: "Startup",
    size: "< 100 Employees",
    price: "$15",
    priceNote: "per user / month",
    pricePrefix: null,
    badge: null,
    features: [
      "Up to 99 employees", "WorkMesh platform access", "Internal Task Marketplace",
      "Skills & Capability Profiles", "AI-powered Matching", "Contributor Dashboard",
      "Task Management", "Standard Support",
    ],
    cta: "Start with WorkMesh",
    action: "register",
  },
  {
    key: "growth",
    title: "Growth",
    size: "100–500 Employees",
    price: "$10",
    priceNote: "per user / month",
    pricePrefix: "Starting from",
    badge: "Best for Growing Organizations",
    features: [
      "Everything in Startup", "100–500 employees", "Enterprise Administration",
      "Team & Department Management", "Advanced Analytics", "Governance Controls",
      "Priority Support",
    ],
    cta: "Contact Sales",
    action: "contact",
  },
  {
    key: "enterprise",
    title: "Enterprise",
    size: "500+ Employees",
    price: "Custom Pricing",
    priceNote: "Let's build a plan for your organization.",
    pricePrefix: null,
    badge: null,
    features: [
      "Everything in Growth", "500+ employees", "Enterprise SSO",
      "Role-Based Access Control", "Advanced Governance", "Custom Integrations",
      "Dedicated Support", "Private Cloud / On-Premise options",
    ],
    cta: "Request a Custom Quote",
    action: "contact",
  },
];

function PricingSection({ onContact }) {
  return (
    <section id="pricing" className="py-24 lg:py-32 border-b border-border" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">06 · Pricing</div>
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
          >
            Simple pricing that scales with your organization.
          </h2>
          <p className="text-zinc-500 mt-4 text-sm leading-relaxed">
            This is a demo preview of WorkMesh — pricing shown is illustrative for the pilot program.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative border rounded-sm p-8 flex flex-col bg-background wm-hover-lift ${
                p.badge ? "border-[#E1252A]" : "border-border"
              }`}
              data-testid={`pricing-card-${p.key}`}
            >
              {p.badge && (
                <div className="absolute -top-3 left-6 bg-[#E1252A] text-white text-[9px] uppercase tracking-[0.22em] px-3 py-1 rounded-sm" data-testid="pricing-badge-growth">
                  {p.badge}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-1">{p.size}</div>
              <h3 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}>
                {p.title}
              </h3>
              <div className="mb-6">
                {p.pricePrefix && (
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{p.pricePrefix}</div>
                )}
                <div
                  className={`font-bold tracking-tight ${p.key === "enterprise" ? "text-3xl" : "text-5xl font-mono"}`}
                  style={p.key !== "enterprise" ? { fontFamily: "'JetBrains Mono', monospace" } : undefined}
                  data-testid={`pricing-price-${p.key}`}
                >
                  {p.price}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{p.priceNote}</div>
              </div>
              <ul className="space-y-2.5 text-sm text-zinc-600 dark:text-zinc-400 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle size={16} weight="duotone" className="text-[#E1252A] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              {p.action === "register" ? (
                <Link to="/register" className="block" data-testid={`pricing-cta-${p.key}`}>
                  <Button className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200 text-[11px] uppercase tracking-[0.22em] h-11">
                    {p.cta} <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
              ) : (
                <Button
                  onClick={onContact}
                  variant={p.badge ? "default" : "outline"}
                  className={`w-full rounded-sm text-[11px] uppercase tracking-[0.22em] h-11 ${
                    p.badge ? "bg-[#E1252A] hover:bg-[#c41f24] text-white" : ""
                  }`}
                  data-testid={`pricing-cta-${p.key}`}
                >
                  {p.cta} <ArrowRight size={14} className="ml-2" />
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-4 text-center" data-testid="pricing-summary">
          {[
            ["< 100 employees", "$15 / user / month"],
            ["100–500 employees", "Starting from $10 / user / month"],
            ["500+ employees", "Custom Pricing"],
          ].map(([a, b]) => (
            <div key={a} className="border border-border rounded-sm py-4 px-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{a}</div>
              <div className="text-sm font-bold mt-1">{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- contact -------------------- */
function ContactSection({ formRef }) {
  const [form, setForm] = useState({
    name: "", work_email: "", company: "", company_size: "51-200", role: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/contact/enterprise`, { ...form, source: "landing" });
      setSent(true);
      toast.success("Thanks — our enterprise team will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" ref={formRef} className="relative py-24 lg:py-32 border-b border-border overflow-hidden" data-testid="contact-section">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle,#E1252A44,transparent 70%)" }} />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">07 · Contact Sales</div>
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-6"
            style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
          >
            Bring WorkMesh<br /> to your organization.
          </h2>
          <p className="text-zinc-500 leading-relaxed mb-8">
            Talk with our team about SSO provisioning, HR system sync, custom
            workflows, and pilot design. Most enterprise pilots go live in under 3 weeks.
          </p>

          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-3">
              <ShieldCheck size={18} className="text-[#E1252A]" />
              <span className="text-zinc-600 dark:text-zinc-400">SSO · SCIM · audit-grade logging</span>
            </div>
            <div className="flex items-center gap-3">
              <Buildings size={18} className="text-[#E1252A]" />
              <span className="text-zinc-600 dark:text-zinc-400">Deployable on your VPC or fully-managed</span>
            </div>
            <div className="flex items-center gap-3">
              <Certificate size={18} className="text-[#E1252A]" />
              <span className="text-zinc-600 dark:text-zinc-400">GDPR & SOC 2 aligned</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {!sent ? (
            <form onSubmit={submit} className="border border-border rounded-sm p-6 lg:p-8 space-y-5 bg-background" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name">
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="contact-name" />
                </Field>
                <Field label="Work email">
                  <Input type="email" required value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} className="rounded-sm" data-testid="contact-email" />
                </Field>
                <Field label="Company">
                  <Input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-sm" data-testid="contact-company" />
                </Field>
                <Field label="Company size">
                  <select
                    value={form.company_size}
                    onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                    className="w-full h-9 border border-border rounded-sm px-3 text-sm bg-background"
                    data-testid="contact-size"
                  >
                    <option>1-50</option><option>51-200</option><option>201-1k</option>
                    <option>1k-5k</option><option>5k-20k</option><option>20k+</option>
                  </select>
                </Field>
              </div>
              <Field label="Your role">
                <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Head of Talent, CHRO…" className="rounded-sm" data-testid="contact-role" />
              </Field>
              <Field label="What are you trying to solve?">
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm min-h-[90px]" placeholder="Any context you'd like to share…" data-testid="contact-message" />
              </Field>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                <Button
                  type="submit" disabled={busy}
                  className="rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200 h-11 px-6 text-[11px] uppercase tracking-[0.22em]"
                  data-testid="contact-submit"
                >
                  {busy ? "Sending…" : "Request Enterprise Demo"} <ArrowRight size={14} className="ml-2" />
                </Button>
                <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Typically respond in {"<"} 24h</span>
              </div>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
              className="border border-border rounded-sm p-10 text-center bg-background"
              data-testid="contact-success"
            >
              <CheckCircle size={48} weight="duotone" className="text-emerald-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2 tracking-tight">Thanks — we&apos;re on it.</h3>
              <p className="text-zinc-500 max-w-md mx-auto">
                Our enterprise team will reach out within one business day with next steps
                and a tailored pilot plan.
              </p>
              <Link to="/login" className="inline-block mt-6" data-testid="contact-try-demo">
                <Button variant="outline" className="rounded-sm text-[11px] uppercase tracking-[0.22em]">
                  Explore the live demo <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

/* -------------------- footer -------------------- */
function Footer() {
  return (
    <footer className="py-14 border-b border-border bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <WorkMeshMark size={28} />
            <span
              className="text-lg font-bold tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk','Satoshi',sans-serif" }}
            >
              Work<span className="text-[#E1252A]">Mesh</span>
            </span>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            The internal talent marketplace for modern enterprises. A VeltrixAI product.
          </p>
        </div>
        <FooterCol title="Product" items={["Capabilities", "AI Team Builder", "Capability Graph", "Live Demo"]} />
        <FooterCol title="Enterprise" items={["SSO Setup", "HR Integrations", "Security", "Pricing"]} />
        <FooterCol title="Company" items={["About", "Careers", "Contact", "Terms"]} />
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-border flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-zinc-500">
        <span>© 2026 WorkMesh · A VeltrixAI product</span>
        <span>Built for enterprise talent teams</span>
      </div>
    </footer>
  );
}
function FooterCol({ title, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-3">{title}</div>
      <ul className="space-y-1.5 text-sm">
        {items.map((i) => (
          <li key={i} className="text-zinc-700 dark:text-zinc-300 hover:text-[#E1252A] cursor-pointer transition-colors">
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------- page -------------------- */
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contactRef = useRef(null);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const scrollToContact = () =>
    contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      <LandingNav onDemoClick={scrollToContact} />
      <Hero onDemoClick={scrollToContact} />
      <TrustBar />
      <FeatureGrid />
      <CapabilityGraphSection />
      <AITeamBuilderSection />
      <ImpactStrip />
      <HowItWorks />
      <AudienceSection />
      <PricingSection onContact={scrollToContact} />
      <ContactSection formRef={contactRef} />
      <Footer />
    </div>
  );
}
